import React, { useContext, useState } from 'react';
import { Button, Flex, Spinner } from '@radix-ui/themes';
import { Sparkles } from 'lucide-react';
import { GlobalContext } from '../GlobalContext';
import { useToast } from '../ToastContext';

/**
 * Shared trigger for all AI features.
 *
 * Returns null when the server has AI features disabled (aiEnabled === false),
 * so callers do not need to guard it themselves.
 *
 * Props:
 *   onGenerate  {() => Promise<void>}  Called when the button is clicked.
 *                                       Must return a promise; AiButton tracks
 *                                       the in-flight state and shows a spinner.
 *   label       {string}               Button text (default "Generate with AI").
 *   disabled    {boolean}              Additional disabled gate (e.g. form not ready).
 *   className   {string}               Extra CSS class(es) forwarded to the button.
 *
 * Error handling:
 *   - err.aiDisabled === true     → distinct "AI features are disabled" warning toast.
 *   - err.budgetExceeded === true → monthly spend cap reached (also a 429, but
 *                                   retrying will not help until next month).
 *   - err.status === 429          → "Too many AI requests" warning toast.
 *   - anything else               → generic error toast with err.message.
 */
export default function AiButton({ onGenerate, label = 'Generate with AI', disabled = false, className }) {
    const { aiEnabled } = useContext(GlobalContext);
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);

    // Hide entirely when the server has AI disabled.
    if (!aiEnabled) return null;

    const handleClick = async () => {
        if (loading || disabled) return;
        setLoading(true);
        try {
            await onGenerate();
        } catch (err) {
            if (err?.aiDisabled) {
                showToast('warning', 'AI features are currently disabled. Please contact your administrator.');
            } else if (err?.budgetExceeded) {
                // Must come BEFORE the generic 429: a spend cap is also a 429,
                // but "wait a moment and try again" would be wrong — the budget
                // does not clear until next month.
                showToast('warning', err.message || 'This month\'s AI budget has been reached. Please contact your administrator.');
            } else if (err?.status === 429) {
                showToast('warning', 'Too many AI requests. Please wait a moment and try again.');
            } else {
                showToast('error', err?.message || 'AI generation failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            type="button"
            variant="soft"
            color="gray"
            size="2"
            disabled={disabled || loading}
            onClick={handleClick}
            className={className}
        >
            <Flex align="center" gap="2">
                {loading
                    ? <Spinner size="1" />
                    : <Sparkles size={14} aria-hidden="true" />
                }
                {loading ? 'Generating…' : label}
            </Flex>
        </Button>
    );
}
